import type { SubAdjustmentRow, SubAdjustmentRowRaw } from '../types'

import * as R from 'ramda'
import { v4 as generateUuid } from 'uuid'

import { castToFloat } from '@app/utils/string-helpers'
import { parseFormula } from '@app/utils/math'

interface SubAdjustmentAccumulator {
  originalAccountBalances: Record<string, number>
  subAdjustments: Record<string, SubAdjustmentRow>
  subAdjustmentOrder: string[]
}

export const formatSubadjustmentData = (
  adjustments: SubAdjustmentRowRaw[]
): SubAdjustmentAccumulator => {
  return R.transduce<
    SubAdjustmentRowRaw,
    SubAdjustmentRow,
    SubAdjustmentAccumulator
  >(
    R.pipe(
      R.map(createSubAdjustment),
      R.map(addDummyAdjustmentId),
      R.filter(isNotEmpty)
    ),
    accumulateSubadjustmentData,
    {
      originalAccountBalances: {},
      subAdjustments: {},
      subAdjustmentOrder: [],
    } as SubAdjustmentAccumulator,
    adjustments
  )
}

const createSubAdjustment = (
  adjustment: SubAdjustmentRowRaw
): SubAdjustmentRow => {
  const subAdjustment: SubAdjustmentRow = {
    id: adjustment.id,
    account: {
      id: adjustment?.account?.accountId,
      actualValue: castToFloat(adjustment?.account?.actualValue),
      name: adjustment?.account?.name,
      number: adjustment?.account?.number,
      originalName: adjustment?.account?.originalName,
      originalNumber: adjustment?.account?.originalNumber,
    },
    actions: null,
    description: adjustment.description,
    valueDebit: adjustment.valueDebit?.toString() ?? null,
    valueCredit: adjustment.valueCredit?.toString() ?? null,
    analyticalDimension0Id: adjustment.analyticalDimension0Id,
    analyticalDimension1Id: adjustment.analyticalDimension1Id,
    analyticalDimension2Id: adjustment.analyticalDimension2Id,
    analyticalDimension3Id: adjustment.analyticalDimension3Id,
  }

  return subAdjustment
}

const isNotEmpty = (
  subadjustment: SubAdjustmentRowRaw | SubAdjustmentRow
): boolean =>
  !!subadjustment &&
  (!!subadjustment.valueDebit ||
    !!subadjustment.valueCredit ||
    !!subadjustment.description ||
    !!subadjustment.account.name ||
    !!subadjustment.account.number)

const addDummyAdjustmentId = (
  adjustment: SubAdjustmentRow
): SubAdjustmentRow => {
  return { ...adjustment, id: adjustment.id ?? generateUuid() }
}

const accumulateSubadjustmentData = (
  acc: SubAdjustmentAccumulator,
  adjustment: SubAdjustmentRow
): SubAdjustmentAccumulator => {
  if (isNotEmpty(adjustment)) {
    acc.subAdjustments[adjustment.id] = adjustment
    acc.subAdjustmentOrder.push(adjustment.id.toString())

    if (adjustment.account.id) {
      acc.originalAccountBalances[adjustment.account.id] = calculateBalance(
        acc,
        adjustment
      )
    }
  }

  return acc
}

const calculateBalance = (
  acc: SubAdjustmentAccumulator,
  adjustment: SubAdjustmentRow
): number => {
  const accountId = adjustment.account.id
  const existingOriginalAccountBalance = accountId
    ? acc.originalAccountBalances?.[accountId]
    : 0

  const effectiveOriginalBalance =
    existingOriginalAccountBalance ?? adjustment?.account?.actualValue

  const rawAccountBalance =
    effectiveOriginalBalance -
    castToFloat(adjustment.valueDebit) +
    castToFloat(adjustment.valueCredit)

  return castToFloat(rawAccountBalance.toFixed(2))
}

export const parseExpressiveValue = (
  value: string | null | undefined
): number => {
  if (!value) {
    return 0
  }

  if (/[+\-*/()]/.test(value)) {
    const parsed = parseFormula(value)
    const nonArrayValue = Array.isArray(parsed) ? parsed[0] : parsed
    const nonInfinityValue = nonArrayValue === Infinity ? 0 : nonArrayValue

    return castToFloat(nonInfinityValue)
  }

  return castToFloat(value)
}
